import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { MOCK } from '@auth/userManager';
import { BaseApiRequest } from '@api/BaseApi';
import { isMissingTokenError } from '@api/apiErrors';

export const fetchSession = createAsyncThunk(
  'session/fetchSession',
  async (signal, { rejectWithValue }) => {
    if (MOCK) {
      return { userId: 'mock', displayName: 'Mock User', company: 'Esgian' };
    }
    try {
      const data = await BaseApiRequest(signal, '/me', 'gpr', 'GET');
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const initialState = {
  userId: null,
  displayName: null,
  company: null,
  status: 'idle'
};

const SessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    resetSession: () => initialState
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSession.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        // /me can resolve without a body (204/empty response). Never dereference the payload
        // blindly: a throw in here aborts the whole immer update, so `status` would stay pinned
        // at 'loading' and PrivateRoute would show the loading screen forever.
        if (!action.payload) {
          state.status = 'error';
          return;
        }
        state.status = 'ok';
        state.userId = action.payload.userId ?? null;
        state.displayName = action.payload.displayName ?? null;
        state.company = action.payload.company ?? null;
        // Persist the user id so DashboardOverviewMap can tell whether the
        // browser-saved `selected-rig` belongs to the current user before
        // restoring it (previously written by the removed AuthSlice.set_user).
        if (state.userId != null) {
          localStorage.setItem('user-id', String(state.userId));
        }
      })
      .addCase(fetchSession.rejected, (state, action) => {
        const status = action.payload?.status;
        if (isMissingTokenError(action.payload)) {
          // No token at all (signed out / logout in flight) — this is not an access problem and
          // not an app error. Terminal on purpose: the sign-in redirect owns what happens next.
          state.status = 'unauthenticated';
        } else if (status === 401 || status === 403) {
          state.status = 'noaccess';
        } else {
          state.status = 'error';
        }
      });
  }
});

export const getSession = (state) => state.session;
export const getSessionStatus = (state) => state.session.status;
export const getCompany = (state) => state.session.company;
export const getDisplayName = (state) => state.session.displayName;
export const getUserId = (state) => state.session.userId;

export const { resetSession } = SessionSlice.actions;

export default SessionSlice.reducer;
